// SimpleLineChart.tsx
import { memo, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Label,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { axisMaxByMSD, toNum } from './chartUtils';
import styled from 'styled-components';

type Props = {
  data: any[];
  dataKey: string; // 'winRate' | 'winPriceRate' | 'avgBidders'
  color: string;
  label: string; // 축 하단 레전드 텍스트
  yLabel: string; // 좌측 축 제목
  unit?: '%' | '명' | '';
  height?: number;
  leftAxisWidth?: number;
};

const H_DEFAULT = 320;
const LEFT_W_DEFAULT = 50;

// legendLabel별 이미지 매핑
const legendImageMap: Record<string, string> = {
  낙찰률: '/assets/images/icon/green_dot.svg',
  낙찰가율: '/assets/images/icon/blue_dot.svg',
  '평균 응찰자 수': '/assets/images/icon/red_dot.svg',
};

function CustomTooltip({
  active,
  payload,
  label,
  unit,
  legendLabel,
}: {
  active?: boolean;
  payload?: any[];
  label?: string;
  unit?: string;
  legendLabel: string;
}) {
  if (!active || !payload || !payload.length) return null;

  const val = payload[0].value;
  const currIdx = payload[0]?.payload?.idx ?? null;
  const dataList = payload[0]?.payload?.dataList || [];
  let periodLabel = label || '';
  if (
    currIdx !== null &&
    Array.isArray(dataList) &&
    dataList.length > 0 &&
    currIdx > 0
  ) {
    const prev = dataList[currIdx - 1]?.label ?? '';
    periodLabel = `${prev} ~ ${label}`;
  } else if (
    currIdx !== null &&
    Array.isArray(dataList) &&
    dataList.length > 0
  ) {
    periodLabel = `${label}`;
  }
  const imgSrc =
    legendImageMap[legendLabel] || '/assets/images/icon/green_dot.svg';

  return (
    <TooltipContainer>
      <TooltipContent>
        <TooltipLabel>{periodLabel}</TooltipLabel>
        <TooltipRow>
          <TooltipLegendWrapper>
            <TooltipImage src={imgSrc} alt="설명 이미지" />
            <TooltipLegend>{legendLabel}</TooltipLegend>
          </TooltipLegendWrapper>
          <TooltipValue>
            {unit === '%'
              ? `${Number(val).toFixed(1)}%`
              : `${Number(val).toFixed(1)}명`}
          </TooltipValue>
        </TooltipRow>
      </TooltipContent>
    </TooltipContainer>
  );
}

function SimpleLineChartBase({
  data,
  dataKey,
  color,
  label,
  yLabel,
  unit = '%',
  height = H_DEFAULT,
  leftAxisWidth = LEFT_W_DEFAULT,
}: Props) {
  const yMax = useMemo(
    () => axisMaxByMSD(Math.max(0, ...data.map((d) => toNum(d[dataKey])))),
    [data, dataKey],
  );

  const innerWidth = useMemo(() => {
    const pointWidth = 64;
    return Math.max(data.length * pointWidth, 300);
  }, [data.length]);

  return (
    <Wrap>
      <Grid style={{ gridTemplateColumns: `${leftAxisWidth}px minmax(0,1fr)` }}>
        {/* 좌측 축만 */}
        <AxisPane>
          <ResponsiveContainer width="100%" height={height}>
            <ComposedChart
              data={data}
              // margin={{ top: 22, right: 10, left: 0, bottom: 38 }}
              margin={{ top: 22, right: 10, left: -10, bottom: 38 }}
            >
              <XAxis dataKey="label" hide />
              <YAxis
                domain={[0, yMax]}
                tick={{ fill: '#787D87', fontSize: 12 }}
                axisLine
                tickLine={{ stroke: '#787D87' }}
                tickFormatter={(v) => Number(v).toLocaleString()}
              >
                <Label
                  value={yLabel}
                  position="insideTopLeft"
                  offset={34}
                  dx={-10}
                  dy={-52}
                  style={{
                    fill: '#787D87',
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                />
              </YAxis>
            </ComposedChart>
          </ResponsiveContainer>
        </AxisPane>

        {/* 가운데 플롯 + X축 */}
        <ScrollPane data-export-expand>
          <Inner
            style={{
              width: innerWidth,
              minWidth: innerWidth,
              height,
            }}
            data-export-inner
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data}
                margin={{ top: 16, right: 24, left: 0, bottom: 0 }}
              >
                <XAxis
                  dataKey="label"
                  axisLine
                  tickLine={{ stroke: '#787D87' }}
                  tick={{ fill: '#787D87', fontSize: 12 }}
                  padding={
                    data.length <= 2
                      ? { left: 48, right: 48 }
                      : { left: 36, right: 36 }
                  }
                  minTickGap={0}
                  interval={0}
                  allowDataOverflow={false}
                />
                <YAxis hide />
                <Tooltip
                  content={({ active, payload, label: tooltipLabel }) => (
                    <CustomTooltip
                      active={active}
                      payload={payload}
                      label={tooltipLabel}
                      unit={unit}
                      legendLabel={label}
                    />
                  )}
                />
                <Line
                  type="linear"
                  dataKey={dataKey}
                  name={label}
                  stroke={color}
                  strokeWidth={2}
                  dot={{
                    r: 4,
                    strokeWidth: 2,
                    stroke: color,
                    fill: color,
                  }}
                  activeDot={{ r: 5 }}
                  legendType="circle"
                />
              </LineChart>
            </ResponsiveContainer>
          </Inner>
        </ScrollPane>
      </Grid>
    </Wrap>
  );
}

export default memo(SimpleLineChartBase);

const Wrap = styled.div`
  width: 100%;
`;
const Grid = styled.div`
  display: grid;
  align-items: start;
  background: #fff;
`;
const AxisPane = styled.div`
  pointer-events: none;
`;
const ScrollPane = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;
const Inner = styled.div`
  padding-bottom: 8px;
  width: 100%;
  min-width: 0;
`;

const TooltipContainer = styled.div`
  display: flex;
  width: 200px;
  height: 92px;
  padding: 20px 16px;
  background: #fff;
  border-radius: 8px;
  opacity: 1;
  position: relative;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);

  align-items: center;
  justify-content: center;
`;

const TooltipImage = styled.img`
  width: 20px;
  height: 20px;
  border-radius: 8px;
  object-fit: cover;
  margin-right: 4px;
`;

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  /* justify-content: center; <-- 삭제 */
  height: 100%;
  /* margin-top: 24px; <-- 삭제 */
  gap: 8px;
  /* 폭 100% 사용 */
  width: 100%;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
`;

const TooltipLegendWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const TooltipLabel = styled.div`
  font-size: 16px;
  font-weight: 500;
  font-style: normal;
  line-height: 24px;
  letter-spacing: -0.02em;
  color: #333;
`;

const TooltipValue = styled.div`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -2%;
`;

const TooltipLegend = styled.div`
  font-weight: 500;
  font-style: Medium;
  font-size: 13px;
  leading-trim: NONE;
  line-height: 20px;
  letter-spacing: -2%;
  color: #424854;
`;
